import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useState } from 'react';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { useGetOrCreateDestination } from '~/hooks/structure-set/useGetOrCreateDestination';

interface UseRingApplicationOptions {
    environment: Environment | null;
    inputRoi: RegionOfInterest | undefined;
    onSuccess: () => void;
    updateView: () => void;
    /**
     * Optional: hand off a cached preview texture instead of recomputing.
     * Returns undefined if no cached result is available for the given input.
     */
    consumeRingPreview?: (
        lowerMm: number,
        upperMm: number
    ) => ContourTexture | undefined;
}

export function useRingApplication({
    environment,
    inputRoi,
    onSuccess,
    updateView,
    consumeRingPreview,
}: UseRingApplicationOptions) {
    const [isProcessing, setIsProcessing] = useState(false);
    const { getOrCreateDestination } = useGetOrCreateDestination({
        environment,
    });

    const handleApplyRing = useCallback(
        async (
            lowerMm: number,
            upperMm: number,
            outputRoiNumber: number | null,
            newName?: string
        ) => {
            if (isProcessing) {
                enqueueSnackbar(
                    'Another ring operation is already in progress.',
                    {
                        variant: 'warning',
                    }
                );
                return;
            }

            if (!environment?.structureSet || !inputRoi?.texture) {
                return;
            }

            setIsProcessing(true);

            try {
                const dest = await getOrCreateDestination(
                    outputRoiNumber,
                    newName
                );
                if (!dest) {
                    setIsProcessing(false);
                    return;
                }

                try {
                    let ringTexture = consumeRingPreview?.(lowerMm, upperMm);
                    if (!ringTexture) {
                        enqueueSnackbar('Applying ring...', {
                            variant: 'info',
                        });
                        // TODO: improve kind of hacky rendering solutions should be removed and
                        // heavy computation should be lifted away from blocking the main thread for smooth rendering.
                        await waitForUI();
                        ringTexture = await SDFOperations.applyRing(
                            inputRoi.texture,
                            lowerMm,
                            upperMm
                        );
                    }
                    dest.replaceTexture(ringTexture, 'Ring');
                    updateView();
                    enqueueSnackbar(`Applied ring to "${dest.name}"`, {
                        variant: 'success',
                    });
                    onSuccess();
                } catch (e) {
                    console.error('Ring apply error:', e);
                    enqueueSnackbar(
                        `Failed to apply ring: ${getErrorMessage(e)}`,
                        { variant: 'error' }
                    );
                } finally {
                    setIsProcessing(false);
                }
            } catch (e) {
                setIsProcessing(false);
                console.error('Ring operation error:', e);
                enqueueSnackbar(`Failed to apply ring: ${getErrorMessage(e)}`, {
                    variant: 'error',
                });
            }
        },
        [
            environment,
            inputRoi,
            isProcessing,
            getOrCreateDestination,
            updateView,
            onSuccess,
            consumeRingPreview,
        ]
    );

    return {
        isProcessing,
        handleApplyRing,
    };
}
